import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  forwardRef,
  Injector,
  TemplateRef,
  inject,
  output,
  input,
  model,
  contentChild,
  viewChild,
} from '@angular/core';
import {
  DropDownFilterSettings,
  MultiSelectComponent as KendoMultiSelectComponent,
  MultiSelectModule,
  PopupSettings,
} from '@progress/kendo-angular-dropdowns';
import {
  AbstractControl,
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { FloatingLabelComponent } from '@progress/kendo-angular-label';
import { DropdownsItemTemplateDirective } from '../../directives/dropdowns-item-template.directive';
import { TagTemplateDirective } from '../../directives/tag-template.directive';
import { NoDataTemplateDirective } from '../../directives/no-data-template.directive';
import { NgTemplateOutlet } from '@angular/common';

@Component({
  selector: 'assist-multiselect',
  templateUrl: './multiselect.component.html',
  styleUrls: ['./multiselect.component.scss'],
  imports: [MultiSelectModule, ReactiveFormsModule, NgTemplateOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiselectComponent),
      multi: true,
    },
  ],
})
export class MultiselectComponent implements OnInit, ControlValueAccessor {
  public floatingLabel = inject(FloatingLabelComponent, { optional: true });
  private controlContainer = inject(ControlContainer, { optional: true, host: true, skipSelf: true });
  private injector = inject(Injector);
  itemTemplate = contentChild(DropdownsItemTemplateDirective, { read: TemplateRef });
  tagTemplate = contentChild(TagTemplateDirective, { read: TemplateRef });
  noDataTemplate = contentChild(NoDataTemplateDirective, { read: TemplateRef });
  kendoMultiselect = viewChild<KendoMultiSelectComponent>('multiSelect');

  open = output<any>();
  filterable = input<boolean>();
  popupSettings = input<PopupSettings>();
  data = input<any[]>();
  placeholder = input<string>();
  dropdownFilter = input<DropDownFilterSettings>();
  textField = input<string>();
  valueField = input<string>();
  allowCustom = input<boolean>();

  value = model<any>();
  valuePrimitive = input<boolean>();
  valueChange = output<any[]>();
  filterChange = output<string>();

  _control: AbstractControl;
  valueNormalizer = input<(any) => any>((x) => x);

  focus() {
    if (this.floatingLabel) {
      this.floatingLabel.focused = true;
    }
  }

  blur() {
    if (this.floatingLabel) {
      this.floatingLabel.focused = false;
    }
  }

  openPopup() {
    this.kendoMultiselect()?.focus();
    this.kendoMultiselect()?.toggle(true);
  }

  ngOnInit(): void {
    const ngControl: NgControl = this.injector.get(NgControl, null);

    if (ngControl == null) {
      this._control = new FormControl<any[]>(this.value());
      return;
    }
    if (ngControl.control) {
      this._control = ngControl.control as FormControl<any[]>;
    } else {
      if (this.controlContainer) {
        this._control = (this.controlContainer.control as any).get(ngControl.name);
      } else {
        throw Error('No control found!');
      }
    }
  }

  registerOnChange(fn: any): void {
    this.valueChange.subscribe(fn);
  }

  registerOnTouched(fn: any): void {}

  writeValue(obj: any): void {
    this.value.set(obj);
  }
}
